import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  Typography
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { TagIcon } from 'lucide-react'
import { useCallback, useContext, useEffect, useReducer, useState } from 'react'
import { toast } from 'react-toastify'
import { ButtonAddNew } from '../../components/ButtonAddNew'
import ConfirmationModal from '../../components/ConfirmationModal'
import { InputSearch } from '../../components/InputSearch'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import QuickTable from '../../components/QuickTable'
import TagModal from '../../components/TagModal'
import { getColorConfig } from '../../components/TagModal/color'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import useHelps from '../../hooks/useHelps'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './style'

const reducer = (state, action) => {
  if (action.type === 'LOAD_TAGS') {
    const tags = action.payload
    const newTags = []

    tags.forEach(tag => {
      const tagIndex = state.findIndex(s => s.id === tag.id)
      if (tagIndex !== -1) {
        state[tagIndex] = tag
      } else {
        newTags.push(tag)
      }
    })

    return [...state, ...newTags]
  }

  if (action.type === 'UPDATE_TAGS') {
    const tag = action.payload
    const tagIndex = state.findIndex(s => s.id === tag.id)

    if (tagIndex !== -1) {
      state[tagIndex] = tag
      return [...state]
    } else {
      return [tag, ...state]
    }
  }

  if (action.type === 'DELETE_TAG') {
    const tagId = action.payload

    const tagIndex = state.findIndex(s => s.id === tagId)
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Tags = () => {
  const classes = useStyles()

  const { user } = useContext(AuthContext)
  // const { id, profile, name } = user

  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedTag, setSelectedTag] = useState(null)
  const [deletingTag, setDeletingTag] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [tags, dispatch] = useReducer(reducer, [])
  const [tagModalOpen, setTagModalOpen] = useState(false)
  const [helps, setHelps] = useState([])

  const { list: listHelps } = useHelps()

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await api.get('/tags/', {
        params: { searchParam, pageNumber }
      })
      dispatch({ type: 'LOAD_TAGS', payload: data.tags, kanban: 0 })
      setHasMore(data.hasMore)
      setLoading(false)
      console.log(data)
    } catch (err) {
      toastError(err)
    }
  }, [searchParam, pageNumber])

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [searchParam])

  useEffect(() => {
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      fetchTags()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [searchParam, pageNumber, fetchTags])

  useEffect(() => {
    const fetchHelps = async () => {
      try {
        const helpsData = await listHelps()
        setHelps(helpsData)
      } catch (err) {
        toastError(err)
      }
    }

    fetchHelps()
  }, [listHelps])

  useEffect(() => {
    const socket = socketManager.GetSocket(user.companyId)

    const onUser = data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_TAGS', payload: data.tags })
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_USER', payload: +data.tagId })
      }
    }

    socket.on('user', onUser)

    return () => {
      socket.disconnect()
    }
  }, [user, socketManager])

  const handleOpenTagModal = () => {
    setSelectedTag(null)
    setTagModalOpen(true)
  }

  const handleCloseTagModal = () => {
    setSelectedTag(null)
    setTagModalOpen(false)
  }

  const handleSearch = event => {
    setSearchParam(event.target.value.toUpperCase())
  }

  const handleEditTag = tag => {
    setSelectedTag(tag)
    setTagModalOpen(true)
  }

  const handleDeleteTag = async tagId => {
    try {
      await api.delete(`/tags/${tagId}`)
      toast.success(i18n.t('tags.toasts.deleted'))
    } catch (err) {
      toastError(err)
    }
    setDeletingTag(null)
    setSearchParam('')
    setPageNumber(1)

    dispatch({ type: 'RESET' })
    setPageNumber(1)
    await fetchTags()
  }

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1)
  }

  const handleScroll = e => {
    if (!hasMore || loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      align: 'left'
    },
    {
      field: 'name',
      headerName: i18n.t('tags.table.name'),
      align: 'left'
    },
    {
      field: 'tipo',
      headerName: 'Tipo',
      align: 'left'
    },
    {
      field: 'ticketsCount',
      headerName: i18n.t('tags.table.tickets'),
      align: 'center'
    }
  ]

  const customCellRenderer = {
    name: tag => {
      const config = getColorConfig(tag.color)

      // CORREÇÃO: Adicionado o 'return' explícito que estava faltando
      return (
        <Chip
          variant="outlined"
          style={{
            backgroundColor: tag.color,
            color: config.textColor,
            borderColor: config.border,
            height: '18px'
          }}
          label={tag.name}
          size="small"
          className={classes.tagsName}
        />
      )
    },
    tipo: tag => <span className={classes.tagType}>{tag.tipo}</span>
  }

  // const customRowClass = (item, index) => {
  //   return index % 2 === 0 ? classes.tableRowEven : classes.tableRowOdd
  // }

  const sortedTags = [...tags].sort((a, b) => b.id - a.id)

  const renderHelpSection = () => {
    if (helps.length === 0) return null

    const tagsHelps = helps.filter(h => h.link === 'Tags')
    if (tagsHelps.length === 0) return null

    return (
      <Box mb={2}>
        <Typography variant="h6" color="primary" gutterBottom>
          📋 Ajuda - Tags
        </Typography>
        {tagsHelps.map((help, index) => (
          <Accordion key={index} style={{ marginBottom: '8px' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{help.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {help.description && (
                  <Typography variant="body2" paragraph>
                    {help.description}
                  </Typography>
                )}
                <Box display="flex" gap={2} mt={1}>
                  {help.video && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        window.open(
                          `https://www.youtube.com/watch?v=${help.video}`,
                          '_blank'
                        )
                      }
                      startIcon={<span>📹</span>}
                    >
                      Ver Tutorial
                    </Button>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    )
  }

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingTag && `${i18n.t('tags.confirmationModal.deleteTitle')}`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t('tags.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        reload={fetchTags}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
        kanban={0}
      />
      <MainHeader
        title={i18n.t('tags.title')}
        description="Organize seus atendimento com tags personalizadas para segmentar e facilitar a gestão do atendimento."
        showTutorial
        // onTutorialClick={handleOpenTutorial}
      >
        <MainHeaderButtonsWrapper>
          <ButtonAddNew
            onClick={handleOpenTagModal}
            startIcon={<TagIcon size={20} />}
            label={i18n.t('tags.buttons.add')}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {renderHelpSection()}

      <Grid container className={classes.boxForms}>
        <Grid item xs={2} className={classes.selectContainer}>
          <InputSearch
            name="searchTags"
            id="searchTags"
            placeholder={i18n.t('contacts.searchPlaceholder')}
            value={searchParam}
            onChange={handleSearch}
          />
        </Grid>
      </Grid>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <QuickTable
          data={sortedTags}
          columns={columns}
          loading={loading}
          customCellRenderer={customCellRenderer}
          actions={
            user.profile === 'admin' || user.profile === 'supervisor'
              ? ['edit', 'delete']
              : []
          }
          onEdit={handleEditTag}
          onDelete={tag => {
            setConfirmModalOpen(true)
            setDeletingTag(tag)
          }}
          emptyMessage={'Nenhum registro encontrado.'}
          containerProps={{ elevation: 0 }}
          showAlternatingRows={true}
          skeletonRows={5}
        />
      </Paper>
    </MainContainer>
  )
}

export default Tags
